import React, { useState, useEffect, useLayoutEffect, useRef, ReactNode, MouseEvent, useMemo } from 'react';
import { createPortal } from 'react-dom';
import './Menu.css';
import cn from 'classnames';

export interface IMenu {
  label: string | ReactNode;
  action?: (data: any) => void;
  subList?: IMenu[];
  postFixComponent?: (index: number, item: IMenu, isOpen: boolean) => ReactNode;
  id?: string;
  value?: string; // Optional parameter for additional item identification
  data?: any; // Allow extra parameters for actions or other purposes
  testId?: string; // data-test-id for testing, allows for easier testing of components
  key?: string; // Optional key for unique identification
  version?: string; // Optional version for tracking changes
  redirectUrl?: string; // Optional redirect url for menu items
  accFun?: (state: boolean) => void;
  accState?: boolean;
  onMouseEnter?: (item: IMenu) => void; // Optional per-item hover callback (e.g. preload-on-hover)
}

interface IMenuItemProps {
  item: IMenu;
  type: 'click' | 'hover';
  subMenuPosition: 'left' | 'right';
  isOpen: boolean;
  onToggle: (index: number, isOpen: boolean) => void;
  index: number;
  reset: () => void;
  testId?: string;
  width?: string; // Optional width for sub-menu items
  maxHeight?: string; // Optional max-height for menu items
  version?: string; // Optional version for tracking changes
  customFunction?: any; // Optional custom function for menu items
}

export const MAX_DEPTH = 5; // Maximum allowable depth for sub-menus

const ASYNC_SELECT_FOCUS_WHITELIST = [
  '#async-select-element-link',
  '.Select__option',
  '.Select__control',
  '.Select__menu',
  '.Portal__option',
  '.Portal__control',
  '.Portal__menu',
  '.Portal__dropdown-indicator',
  '.Select__dropdown-indicator'
];

const isAsyncSelectElement = (node: HTMLElement | null): boolean => {
  if (!node) return false;
  return ASYNC_SELECT_FOCUS_WHITELIST.some((selector) => node.closest(selector));
};

/**
 * Recursively calculates the maximum depth of sub-lists.
 * 
 * @param menu The menu item to calculate depth for.
 * @returns The depth of the menu item’s sub-lists.
 */
const getSubListDepth = (menu: IMenu): number => {
  // If there are no sub-lists, the depth is 1
  if (!menu.subList || menu.subList.length === 0) return 1;
  // Recursively find the maximum depth among the sub-lists
  return 1 + Math.max(...menu.subList.map(getSubListDepth));
};

const MenuItem: React.FC<IMenuItemProps> = ({
  item,
  type,
  subMenuPosition,
  isOpen,
  onToggle,
  index,
  reset,
  width,
  maxHeight,
  version,
  customFunction
}) => {
  const [openedIndex, setOpenedIndex] = useState<number | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const isElementFocused = useRef(null);

  /**
   * Toggles the open state for the current menu item.
   * 
   * @param index The index of the menu item.
   * @param isOpen Whether the menu item is open.
   */
  const handleToggle = (index: number, isOpen: boolean) => {
    setOpenedIndex(isOpen ? index : null);
  };

  const dropdownRef = useRef<HTMLLIElement | null>(null);

  /**
   * Handles the mouse enter event for hover type menus.
   * Opens the dropdown menu if the type is 'hover'.
   */
  const handleMouseEnter = () => {
    item.onMouseEnter?.(item);
    if (type === 'hover') {
      onToggle(index, true);
      setIsDropdownOpen(true);
      customFunction && customFunction();
    }
  };

  /**
   * Handles the mouse leave event for hover type menus.
   * Closes the dropdown menu if the type is 'hover'.
   */
  const handleMouseLeave = () => {
    if (type === 'hover') {
      onToggle(index, false);
      setIsDropdownOpen(false);
    }
  };

  /**
   * Handles the click event for menu items.
   * Toggles sub-menu visibility if applicable, or triggers an action if no sub-lists are present.
   * 
   * @param e The mouse event that triggered the function.
   */
  const handleClick = (e: MouseEvent<HTMLLIElement>) => {
    e.stopPropagation(); // Prevent event propagation to parent elements
    if (type === 'click' && item.subList?.length) {
      // Toggle the menu open state on click if it has sub-lists
      onToggle(index, !isOpen);
      setIsDropdownOpen(!isDropdownOpen);
      setOpenedIndex(null);
      customFunction && customFunction();
    }
    if (!item?.subList?.length && typeof item.action === 'function') {
      // Call the action function if the item has no sub-lists
      item.action({ ...item, index });
      reset(); // Reset the menu state after action
    }
  };

  const handleSubMenuBlur = (e: React.FocusEvent<HTMLUListElement>) => {
    // relatedTarget = the next focused element
    const next = e.relatedTarget as HTMLElement | null;
  
    if (isAsyncSelectElement(next)) {
      return;
    }
  
    // If next focus is outside THIS submenu (or focus is lost entirely)
    if (!next || !e.currentTarget.contains(next)) {
      // Move focus back to the parent <li>
      dropdownRef.current?.focus();
      // Explicitly close the submenu instead of toggling to avoid accidental re-open
      setIsDropdownOpen(false);
    }
  };

  useEffect(() => {
    if (isDropdownOpen) {
      isElementFocused.current?.firstElementChild?.focus();
    }
  }, [isDropdownOpen]);

  const subMenuHandlerForTLN = (key: string, item: IMenu) => {
    if (key === "ArrowRight") {
      item.accFun && item.accFun(true);

      const element = document.getElementById('async-select-element-link');
      element?.focus();
    } else if (key === "ArrowLeft" || key === "Escape") {
      item.accFun && item.accFun(false);
      setIsDropdownOpen(false);
    } 
  }

  return (
    <li
      className={`menu-item ${isOpen ? 'menu-item--active' : ''}`}
      ref={dropdownRef}
      id={item.id} // Apply id to the menu item for unique identification
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      onKeyDown={(e) => {
        item.accFun && subMenuHandlerForTLN(e.key, item);
        e.preventDefault();
        e.stopPropagation();

        if (e.key === 'ArrowRight') {

          onToggle(index, true);
          setIsDropdownOpen(true);
          customFunction && customFunction();
        } else if (e.key === "ArrowLeft") {

        
          if (isDropdownOpen) {
            // only close if currently open
            setIsDropdownOpen(false);
            onToggle(index, false);
          } else {
            // focus back to parent level
            dropdownRef.current?.parentElement?.closest('li')?.focus();
          }
        } else if (e.key === "ArrowDown") {


          if (isDropdownOpen) {
            // Move into submenu
            const firstChild = isElementFocused.current?.firstElementChild;
            if (firstChild) {
              (firstChild as HTMLElement).focus();
              return;
            }
          }

          // Move to next menu item in same level
          const nextSibling = dropdownRef.current?.nextElementSibling;
          if (nextSibling) {
            (nextSibling as HTMLElement).focus();
          }

          if (!nextSibling) {
            if (!isDropdownOpen) {
              setIsDropdownOpen(!isDropdownOpen);
              dropdownRef.current?.parentElement?.closest('li')?.focus();
            }
          }
        } else if (e.key === "ArrowUp" && !item.accState) {
        
          // 1️⃣ If submenu is open → move to LAST child
          if (isDropdownOpen) {
            const lastChild = isElementFocused.current?.lastElementChild;
            if (lastChild) {
              (lastChild as HTMLElement).focus();
              return;
            }
          }
        
          // // 2️⃣ Move to previous sibling in SAME LEVEL
          const prevSibling = dropdownRef.current?.previousElementSibling;
          if (prevSibling) {
            (prevSibling as HTMLElement).focus();
          }
        
          // // 3️⃣ If no previous sibling → go to PARENT LI
          const parentLi = dropdownRef.current
            ?.closest("ul")    // find parent <ul>
            ?.closest("li");   // find parent <li>
        
          if (parentLi) {
            (parentLi as HTMLElement).focus();
          }
        } else if (e.key === "Enter") {
          item.action({ ...item, index });
          reset();
        } else if (e.key === "Escape") {
          reset();
        }
      }}
      data-test-id={item.testId} // Set data-test-id for testing
      tabIndex={1} // Make the menu item focusable
      aria-expanded={isOpen} // Indicates whether the item is expanded
      role="button"
    >
      <a className={`menu-option ${isOpen ? 'active' : ''}`} href={item.redirectUrl && item.redirectUrl} onClick={(e) => e.preventDefault()}>
        <div className={version === "v2" && `menu-option-label`}>
          {item.label}
        </div>
        {item.postFixComponent && (
          <div className='post-fix'>
            {item.postFixComponent(index, item, isOpen)}
          </div>
        )}
      </a>
      {(isOpen && isDropdownOpen && item?.subList?.length) ? (
        <ul
          className={`menu-content ${subMenuPosition}`} // Position sub-menu based on 'left' or 'right'
          style={{ width: width || "300px", ...(version === "v2" && maxHeight && { maxHeight: maxHeight }) }}
          data-test-id={`${item.testId}-child`} // Set data-test-id for child menu items
          ref={isElementFocused}
          onBlur={handleSubMenuBlur}
        >
          {item.subList.map((subItem, subIndex) => (
            <MenuItem
              key={subItem.key || `menu-item-${index}-${subIndex}`} // Use key if present, otherwise use index
              item={subItem}
              isOpen={openedIndex === subIndex}
              type={type}
              subMenuPosition={subMenuPosition}
              onToggle={handleToggle}
              index={subIndex}
              reset={reset}
              width={width}
              maxHeight={maxHeight}
              version={version}
              customFunction={customFunction}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
};

export interface IMenuProps {
  list: IMenu[];
  type: 'click' | 'hover';
  subMenuPosition: 'left' | 'right';
  children: ReactNode;
  testId?: string;
  width?: string; // Optional width for the main menu
  version?: string; // Optional version for tracking changes
  maxHeight?: string; // Optional max-height for menu items
  customFunction?: any; // Optional trigger function for menu items
  disabled?: boolean;
  menuButtonClickFunction?: (e: any) => Promise<void>;
  renderInPortal?: boolean; // render the component as a portal
  portalTarget?: HTMLElement; // Optional portal mount node 
  portalClassName?: string; // Optional wrapper class for the portaled content
}

const Menu: React.FC<IMenuProps> = ({
  list,
  type,
  subMenuPosition,
  children,
  testId,
  width,
  maxHeight,
  version,
  customFunction,
  disabled,
  menuButtonClickFunction,
  renderInPortal = false,
  portalTarget,
  portalClassName
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [openedIndex, setOpenedIndex] = useState<number | null>(null);

  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const dropdownElement = useRef<any>(null);
  const [portalCoords, setPortalCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  /**
   * Resets the menu state by closing the dropdown and clearing the opened index.
   */
  const reset = () => {
    setIsDropdownOpen(false);
    setOpenedIndex(null);
  };

  /**
   * Handles mouse enter event for hover type menus.
   * Opens the dropdown menu if the type is 'hover'.
   */
  const handleMouseEnter = () => {
    if (type === 'hover' && !disabled) {
      setIsDropdownOpen(true);
    }
  };

  /**
   * Handles mouse leave event for hover type menus.
   * Closes the dropdown menu if the type is 'hover'.
   */
  const handleMouseLeave = () => {
    if (type === 'hover' && !disabled) {
      setIsDropdownOpen(false);
    }
  };

  /**
   * Handles click event for the main menu.
   * Toggles the visibility of the main dropdown.
   * 
   * @param e The mouse event that triggered the function.
   */
  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation(); // Prevent event propagation to parent elements
    if (disabled) return;
    setIsDropdownOpen(!isDropdownOpen);
    if (menuButtonClickFunction) {
      menuButtonClickFunction(e);
    }
    setOpenedIndex(null);
  };

  /**
   * Handles clicks outside the menu to close it.
   * 
   * @param e The mouse event that triggered the function.
   */
  const handleClickOutside = (event: MouseEvent | KeyboardEvent) => {
    if (disabled) return;

    if (event.type === 'keydown') {
      const { key } = event as KeyboardEvent;
      if (key !== 'Escape') {
        return;
      }
    } else if (event.type === 'click') {
      const target = event.target as HTMLElement | null;

      if (target && (dropdownRef.current?.contains(target) ||
        (renderInPortal && dropdownElement.current?.contains(target)))) {
        return;
      }

      if (isAsyncSelectElement(target)) {
        return;
      }
    } else {
      return;
    }

    setIsDropdownOpen(false);
    setOpenedIndex(null);
  };

  /**
   * Handles toggling of menu items.
   * 
   * @param index The index of the menu item.
   * @param isOpen Whether the menu item is open.
   */
  const handleToggle = (index: number, isOpen: boolean) => {
    setOpenedIndex(isOpen ? index : null);
  };

  // Effect to add/remove event listeners for click and keydown
  useEffect(() => {
    if (isDropdownOpen) {
      document.addEventListener('click', handleClickOutside as EventListener);
      document.addEventListener('keydown', handleClickOutside as EventListener);
    } else {
      document.removeEventListener('click', handleClickOutside as EventListener);
      document.removeEventListener('keydown', handleClickOutside as EventListener);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside as EventListener);
      document.removeEventListener('keydown', handleClickOutside as EventListener);
    };
  }, [isDropdownOpen]);

  useEffect(() => {
    if (isDropdownOpen) {
      dropdownElement.current?.firstElementChild?.focus();
    }
  }, [isDropdownOpen]);

  /**
   * When rendering the dropdown in a portal, the <ul> is detached from the .menu
   * wrapper so the CSS `position: absolute` anchor is gone. Compute fixed-viewport
   * coordinates from the trigger (.menu) rect and keep them in sync on scroll/resize.
   * Uses a layout effect to position before paint (no first-frame flash at 0,0).
   */
  useLayoutEffect(() => {
    if (!renderInPortal || !isDropdownOpen) return;

    const computePosition = () => {
      const anchor = dropdownRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const menuWidth = parseInt(width ?? '293', 10) || 293;
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - menuWidth - 8));
      setPortalCoords({ top: rect.bottom, left });
    };

    computePosition();
    window.addEventListener('scroll', computePosition, { capture: true, passive: true });
    window.addEventListener('resize', computePosition);
    return () => {
      window.removeEventListener('scroll', computePosition, { capture: true } as EventListenerOptions);
      window.removeEventListener('resize', computePosition);
    };
  }, [renderInPortal, isDropdownOpen, width]);

  /**
   * Calculates the maximum depth of the menu's sub-lists.
   * 
   * @returns The maximum depth of the menu.
   */
  const overallDepth = useMemo(() => Math.max(...list.map(getSubListDepth)), [list]);

  /**
   * Warns if the depth exceeds the maximum limit.
   */
  useEffect(() => {
    if (overallDepth > MAX_DEPTH) {
      console.warn(`Warn: SubList depth exceeds the maximum limit of ${MAX_DEPTH}`);
    }
  }, [overallDepth]);

  useEffect(() => {
    if (!isDropdownOpen) {
      list.forEach((menuItem) => {
        menuItem.accFun?.(false);
      });
    }
  }, [isDropdownOpen, list]);

  const handleSubMenuBlur = (e: React.FocusEvent<HTMLUListElement>) => {
    // relatedTarget = the next focused element
    const next = e.relatedTarget as HTMLElement | null;

    if (isAsyncSelectElement(next)) {
      return;
    }
  
    // If next focus is outside THIS submenu (or focus is lost entirely)
    if (!next || !e.currentTarget.contains(next)) {
      // Move focus back to the parent <li>
      dropdownRef.current?.focus();
      // Explicitly close the menu instead of toggling to avoid accidental re-open
      setIsDropdownOpen(false);
    }
  };

  return (
    <div
      className={cn(
        'menu',
        {
          'menu__v2': version === 'v2',
          'menu__disabled': disabled
        }
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={dropdownRef}
      onClick={handleClick}
      data-test-id={testId || "menu"} // Set data-test-id for the main menu
      tabIndex={0} // Make the menu item focusable
      aria-expanded={isDropdownOpen} // Indicates whether the item is expanded
      role='menu'
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          setIsDropdownOpen(false);
          dropdownRef.current?.focus();
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          e.stopPropagation();
          setIsDropdownOpen(true);
          dropdownElement.current?.firstElementChild?.focus();
        } else if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          if (disabled) return;
          if (menuButtonClickFunction) {
            menuButtonClickFunction(e);
          }
          setIsDropdownOpen(false);
          setOpenedIndex(null);
        }
      }}
    >
      <div className={`menu-item-button ${disabled ? 'disabled' : ''}`}>{children}</div>
      {isDropdownOpen && (() => {
        const menuList = (
          <ul
            className={`menu-content menu-content-${type}${renderInPortal ? ` menu-content--portal${version === "v2" ? " menu-content--portal-v2" : ""}` : ""}`} // Apply style based on menu type
            style={{ width: width || "293px", ...(version === "v2" && maxHeight && { maxHeight: maxHeight }), ...(renderInPortal && { position: "fixed", top: portalCoords.top, left: portalCoords.left, right: "auto", zIndex: 9999 }) }} // Default width if none provided
            data-test-id='menu-child' // Set data-test-id for child menu items
            ref={dropdownElement}
            onBlur={handleSubMenuBlur}
          >
            {list.map((item, index) => (
              <MenuItem
                key={item.key || `menu-item-${index}`} // Use key if present, otherwise use index
                item={item}
                type={type}
                subMenuPosition={subMenuPosition}
                isOpen={openedIndex === index}
                onToggle={handleToggle}
                index={index}
                width={width}
                reset={reset}
                maxHeight={maxHeight}
                version={version}
                customFunction={customFunction}
              />
            ))}
          </ul>
        );
        
        if (!renderInPortal) return menuList;
        return createPortal(
          portalClassName ? <div className={portalClassName}>{menuList}</div> : menuList,
          portalTarget ?? document.body
        );
      })()}
    </div>
  );
};

export default Menu;
